import {
  Currency,
  CurrencyAmount,
  Fraction,
  Pair,
  Percent,
  Token,
  Trade,
  TradeType,
  BIG_10000,
} from '@nominex/nomiswap-sdk'
import { LP_FEE_FRACTION, REFUNDABLE_FRACTION } from 'config/constants/exchange'
import { NMX } from 'config/tokens'
import {
  BLOCKED_PRICE_IMPACT_NON_EXPERT,
  ALLOWED_PRICE_IMPACT_HIGH,
  ALLOWED_PRICE_IMPACT_LOW,
  ALLOWED_PRICE_IMPACT_MEDIUM,
} from 'config/constants'
import { Field } from 'state/swap/actions'
import { basisPointsToPercent } from 'utils'

const ONE_HUNDRED_PERCENT = new Percent(BIG_10000, BIG_10000)

// for each hop in our trade, take away the x*y=k price impact from 0.1% fees
// e.g. for 3 tokens/2 hops: 1-((1-0.1)*(1-0.1))
function calculateRealizedTradeFee(routePairs: Pair[]): Percent {
  return ONE_HUNDRED_PERCENT.subtract(
    routePairs
      .map((pair) => pair.getSwapFeePercent())
      .reduce<Percent>(
        (currentFee: Percent, baseFee: Percent): Percent => currentFee.multiply(ONE_HUNDRED_PERCENT.subtract(baseFee)),
        ONE_HUNDRED_PERCENT,
      ),
  )
}

function calculateRealizedTradeFeeAmount(
  trade: Trade<Currency, Currency, TradeType>,
  realizedTradeFee: Fraction,
): CurrencyAmount<Currency> {
  return CurrencyAmount.fromRawAmount(
    trade.inputAmount.currency,
    realizedTradeFee.multiply(trade.inputAmount.quotient).quotient,
  )
}

function calculateRealizedLpFeeAmount(trade: Trade<Currency, Currency, TradeType>): Percent {
  const pairsWithLpFees = trade?.route.pairs.filter((pair: Pair) => pair.involvesToken(NMX[pair.token0.chainId])) ?? []
  const realizedTradeFee = calculateRealizedTradeFee(pairsWithLpFees)

  return realizedTradeFee.multiply(LP_FEE_FRACTION)
}

// computes price breakdown for the trade
export function computeTradePriceBreakdown(trade: Trade<Currency, Currency, TradeType>): {
  priceImpactWithoutFee: Percent | null
  realizedTradeFee: CurrencyAmount<Token> | null
  realizedLpFee: Fraction | null
  tradeFee: Percent | null
} {
  const tradePriceBreakdown = {
    priceImpactWithoutFee: null,
    realizedTradeFee: null,
    realizedLpFee: null,
    tradeFee: null,
  }

  if (trade) {
    const realizedTradeFee = calculateRealizedTradeFee(trade.route.pairs)
    const priceImpactWithoutFeeFraction = trade.priceImpact.subtract(realizedTradeFee) // remove fees from price impact

    tradePriceBreakdown.tradeFee = realizedTradeFee
    tradePriceBreakdown.realizedTradeFee = calculateRealizedTradeFeeAmount(trade, realizedTradeFee)
    tradePriceBreakdown.realizedLpFee = calculateRealizedLpFeeAmount(trade)
    tradePriceBreakdown.priceImpactWithoutFee = new Percent(
      priceImpactWithoutFeeFraction?.numerator,
      priceImpactWithoutFeeFraction?.denominator,
    ) // the x*y=k impact
  }

  return tradePriceBreakdown
}

// computes nmx cashback for the trade
export function computeCashback(trade: Trade<Currency, Currency, TradeType>, cashbackFraction: Fraction): Fraction {
  const pairsWithCashback =
    trade?.route.pairs.filter((pair: Pair) => pair.involvesToken(NMX[pair.token0.chainId])) ?? []
  const realizedTradeFee = calculateRealizedTradeFee(pairsWithCashback)
  const realizedTradeFeeAmount = calculateRealizedTradeFeeAmount(trade, realizedTradeFee)

  return realizedTradeFeeAmount
    ? realizedTradeFeeAmount.multiply(REFUNDABLE_FRACTION).multiply(cashbackFraction)
    : new Fraction('0')
}

// computes the minimum amount out and maximum amount in for a trade given a user specified allowed slippage in bips
export function computeSlippageAdjustedAmounts(
  trade: Trade<Currency, Currency, TradeType> | undefined,
  allowedSlippage: number,
): { [field in Field]?: CurrencyAmount<Currency> } {
  const pct = basisPointsToPercent(allowedSlippage)
  return {
    [Field.INPUT]: trade?.maximumAmountIn(pct),
    [Field.OUTPUT]: trade?.minimumAmountOut(pct),
  }
}

export function warningSeverity(priceImpact: Percent | undefined): 0 | 1 | 2 | 3 | 4 {
  if (!priceImpact?.lessThan(BLOCKED_PRICE_IMPACT_NON_EXPERT)) return 4
  if (!priceImpact?.lessThan(ALLOWED_PRICE_IMPACT_HIGH)) return 3
  if (!priceImpact?.lessThan(ALLOWED_PRICE_IMPACT_MEDIUM)) return 2
  if (!priceImpact?.lessThan(ALLOWED_PRICE_IMPACT_LOW)) return 1
  return 0
}

export function formatExecutionPrice(trade?: Trade<Currency, Currency, TradeType>, inverted?: boolean): string {
  if (!trade) {
    return ''
  }
  return inverted
    ? `${trade.executionPrice.invert().toSignificant(6)} ${trade.inputAmount.currency.symbol} / ${
        trade.outputAmount.currency.symbol
      }`
    : `${trade.executionPrice.toSignificant(6)} ${trade.outputAmount.currency.symbol} / ${
        trade.inputAmount.currency.symbol
      }`
}
